import { uiFormat, uiText } from '../../i18n';
import { record, rows, text } from './studio-content';

export function studioOrderSteps(order: unknown): Array<{ key: string; title: string; status: string }> {
  return rows(record(order).steps).flatMap(step => {
    const title = text(step.status === 'done' && step.title_done ? step.title_done : step.title);
    return title ? [{ key: text(step.key) || title, title, status: text(step.status) || 'pending' }] : [];
  });
}
/** Размер рамки героя по пропорциям заказа («3/4», «9/16»): вписывается в коробку 236 × 300, как у BotRun (circle/OrderTail).
    Одного aspect-ratio мало — у .pr-f нет ширины в сетке .pr-hero, и рамка схлопывалась в ноль. */
export function studioFrameSize(ratio: string) {
  const [w, h] = ratio.split('/').map(Number);
  const value = w > 0 && h > 0 ? w / h : 3 / 4;
  return value >= 236 / 300 ? { width: 236, height: Math.round(236 / value) } : { width: Math.round(300 * value), height: 300 };
}
/** Чек под итогом: «Списано N C · остаток M C», со скидкой — с водяным знаком; после сбоя — «Вернули N C». */
export function studioOrderReceipt(order: unknown, watermark: string): string {
  const source = record(order);
  const charge = record(source.charge);
  const balance = record(source.balance).circle;
  const financial = record(source.financial);
  const debited = typeof charge.debited === 'number' ? charge.debited : financial.state === 'debited' ? Number(financial.amount_tokens) : NaN;
  const refunded = typeof charge.refunded === 'number' ? charge.refunded : financial.state === 'refunded' ? Number(financial.amount_tokens) : NaN;
  const rest = typeof balance === 'number' ? uiFormat(' · остаток {0} C', balance.toLocaleString('ru-RU')) : '';
  if (Number.isFinite(refunded) && refunded > 0) return uiFormat('Вернули {0} C', refunded) + rest;
  if (financial.state === 'dev_free') return uiText('Тестовый заказ без списания');
  if (!Number.isFinite(debited) || debited <= 0) return '';
  const cheap = record(source.display).cheap === true;
  return (cheap ? uiFormat('Списано {0} C со скидкой, {1} водяной знак', debited, watermark) : uiFormat('Списано {0} C', debited)) + rest;
}

/** Что сказать о чате на итоге готового заказа (APP-SPEC-5 §1.4, §16; Q-221, Q-391):
    `sent` — бот уже прислал; `pending` — доставка запрошена или ещё не известна («Бот пришлёт»);
    `button` — экран с отправкой по кнопке итога («Сменить голос в записи», «Почистить запись»);
    `settings` — бот не присылает, так выбрано в настройках (`delivery.reason = disabled_by_settings`);
    `off` — доставка не запрошена по другой причине: обещать бота нельзя.
    Готовый заказ с автодоставкой сразу получает запрос (pending), поэтому `not_requested` у готового — «бот сам не пришлёт». */
export type StudioDeliveryView = 'sent' | 'pending' | 'button' | 'settings' | 'off';
const ON_REQUEST_SCREENS = ['voice', 'voice_clean'];
export function studioDeliveryView(order: unknown): StudioDeliveryView {
  const source = record(order);
  const delivery = record(source.delivery);
  const state = text(delivery.state);
  if (state === 'sent' || state === 'delivered') return 'sent';
  if (state !== 'not_requested' || source.status !== 'done') return 'pending';
  if (delivery.reason === 'on_request' || ON_REQUEST_SCREENS.includes(text(source.screen))) return 'button';
  return delivery.reason === 'disabled_by_settings' ? 'settings' : 'off';
}
/** Подпись итога, когда бот сам не присылает; null — действует подпись экрана */
export function studioDeliveryOffText(view: StudioDeliveryView): string | null {
  if (view === 'settings') return 'Результат сохранён в «Истории». В чат не отправляем — так выбрано в настройках.';
  if (view === 'off') return 'Результат сохранён в «Истории».';
  return null;
}
